import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Trash2, Plus, UserPlus } from "lucide-react";
import { toast } from "sonner";

const METHOD_LABEL = { mobile_money: "Mobile Money", mpesa: "M-Pesa", bank_deposit: "Bank Deposit", cash_pickup: "Cash Pickup" };

const Recipients = () => {
  const [list, setList] = useState([]);
  const [countries, setCountries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", country_code: "", delivery_method: "", account_identifier: "", bank_name: "" });

  const load = () => api.get("/recipients").then(r => setList(r.data));
  useEffect(() => {
    load();
    api.get("/countries").then(r => setCountries(r.data));
  }, []);

  const country = countries.find(c => c.code === form.country_code);
  const methods = country?.methods || [];

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/recipients", form);
      toast.success("Recipient added");
      setOpen(false);
      setForm({ full_name: "", country_code: "", delivery_method: "", account_identifier: "", bank_name: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add recipient"); }
  };

  const remove = async (id) => {
    await api.delete(`/recipients/${id}`);
    toast.success("Recipient removed");
    load();
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="recipients-root">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3 fade-up">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-accent)] font-semibold mb-1.5">Beneficiaries</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Your recipients</h1>
          <p className="text-[var(--ra-muted)] mt-1">Save loved ones once and send instantly any time.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary rounded-md h-11" data-testid="add-recipient-btn"><Plus size={16} className="mr-2" />Add recipient</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">New recipient</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="recipient-form">
              <Field id="full_name" label="Full name" value={form.full_name} onChange={(v) => setForm(s => ({ ...s, full_name: v }))} testid="rcp-fullname" />
              <div>
                <Label className="text-sm text-[var(--ra-muted)]">Country</Label>
                <Select value={form.country_code} onValueChange={(v) => setForm(s => ({ ...s, country_code: v, delivery_method: "" }))}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-md" data-testid="rcp-country"><SelectValue placeholder="Choose country" /></SelectTrigger>
                  <SelectContent>
                    {countries.map(c => <SelectItem key={c.code} value={c.code} data-testid={`country-opt-${c.code}`}>{c.flag} {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {methods.length > 0 && (
                <div>
                  <Label className="text-sm text-[var(--ra-muted)]">Delivery method</Label>
                  <Select value={form.delivery_method} onValueChange={(v) => setForm(s => ({ ...s, delivery_method: v }))}>
                    <SelectTrigger className="mt-1.5 h-11 rounded-md" data-testid="rcp-method"><SelectValue placeholder="Choose method" /></SelectTrigger>
                    <SelectContent>
                      {methods.map(m => <SelectItem key={m} value={m} data-testid={`method-opt-${m}`}>{METHOD_LABEL[m]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Field id="account_identifier" label={form.delivery_method === "bank_deposit" ? "Account number" : "Phone / Account"}
                value={form.account_identifier} onChange={(v) => setForm(s => ({ ...s, account_identifier: v }))} testid="rcp-account" />
              {form.delivery_method === "bank_deposit" && (
                <Field id="bank_name" label="Bank name" value={form.bank_name} onChange={(v) => setForm(s => ({ ...s, bank_name: v }))} testid="rcp-bank" />
              )}
              <DialogFooter>
                <Button type="submit" className="btn-primary rounded-md w-full h-11" data-testid="rcp-submit">Save recipient</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {list.length === 0 ? (
        <div className="ra-card p-12 text-center fade-up fade-up-2" data-testid="recipients-empty">
          <UserPlus className="mx-auto text-[var(--ra-muted)]" size={40} />
          <h3 className="font-heading text-lg font-semibold mt-3">No recipients yet</h3>
          <p className="text-sm text-[var(--ra-muted)] mt-1 max-w-sm mx-auto">Add a friend or family member to start sending money in seconds.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((r) => (
            <div key={r.id} className="ra-card p-5 flex flex-col fade-up" data-testid={`recipient-card-${r.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[var(--ra-accent)]/15 text-[var(--ra-accent)] font-bold flex items-center justify-center text-lg">{r.full_name[0]?.toUpperCase()}</div>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{r.full_name}</div>
                  <div className="text-xs text-[var(--ra-muted)]">{r.country_code} · {r.currency}</div>
                </div>
              </div>
              <div className="mt-4 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-[var(--ra-muted)]">Method</span><span className="font-medium">{METHOD_LABEL[r.delivery_method]}</span></div>
                <div className="flex justify-between"><span className="text-[var(--ra-muted)]">Account</span><span className="font-medium truncate ml-2">{r.account_identifier}</span></div>
                {r.bank_name && <div className="flex justify-between"><span className="text-[var(--ra-muted)]">Bank</span><span className="font-medium">{r.bank_name}</span></div>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="mt-4 text-[var(--ra-error)] hover:bg-[var(--ra-error)]/8 hover:text-[var(--ra-error)] self-start" data-testid={`recipient-delete-${r.id}`}>
                <Trash2 size={14} className="mr-1.5" />Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ id, label, value, onChange, testid }) => (
  <div>
    <Label htmlFor={id} className="text-sm text-[var(--ra-muted)]">{label}</Label>
    <Input id={id} value={value} required onChange={(e) => onChange(e.target.value)} className="mt-1.5 h-11 rounded-md" data-testid={testid} />
  </div>
);

export default Recipients;
